import type { QueuedOrder } from '../types';

/**
 * Is this row's work already done, even though its production row still says
 * it has not started?
 *
 * The backend resolves an order to 'IN_PRODUCTION' when its live garment units
 * already cover every line. That happens two ways:
 *   - the ordinary way: the order really is batched, and its production status
 *     says so (Printing, Binned, and so on). Those rows keep their real stage
 *     badge and were never selectable anyway.
 *   - the stranded way: the parent row was reset to NEW with no batch (routing
 *     an order to another floor does exactly this) while its only unit lives on
 *     inside another floor's batch. Nothing on the parent row shows it, so the
 *     queue used to offer the order as Ready and Create Batch then refused it.
 *
 * This predicate names the second case. It is shared by the row (badge,
 * disabled checkbox) and the page (select-all): if the two disagreed, a row
 * could render an enabled checkbox whose clicks are silently dropped.
 */
export function isStrandedInProduction(order: Pick<QueuedOrder, 'eligibility_status' | 'prod_order'>): boolean {
  if (order.eligibility_status !== 'IN_PRODUCTION') return false;
  const status = order.prod_order?.production_status;
  return !status || status === 'NEW' || status === 'WAITING_STOCK';
}

/**
 * Is this order blocked by stock? The backend decides (`stock_blocked`: out of
 * stock now, or an open out-of-stock issue, and not priority) with the same
 * rule its Ready tab filters on. The fallback is the old badge rule, and only
 * applies while this frontend talks to a backend that does not send the field.
 */
export function isStockBlocked(
  order: Pick<QueuedOrder, 'stock_blocked' | 'eligibility_status' | 'is_priority'>
): boolean {
  return order.stock_blocked ?? (order.eligibility_status === 'OUT_OF_STOCK' && !order.is_priority);
}

/**
 * Rows Create Batch would reject, so the queue must not let them be selected.
 * Held orders are excluded by the caller, which has the live countdown.
 *
 * BLU-1506: a `notBatchable` row (a client's own order the floor's owner is
 * not ready to batch) is never selectable either: not by a click, not by
 * select-all or a shift range, and so never a hand-pick added to a batch
 * scope. The server leaves it out of every scope as well.
 */
export function isSelectableForBatching(
  order: Pick<QueuedOrder, 'eligibility_status' | 'prod_order' | 'notBatchable'>,
): boolean {
  return order.notBatchable !== true && !isStrandedInProduction(order);
}
